import React from "react";
import {
  Crown,
  Users,
  School,
  BookOpen,
  Wrench,
  Package,
  Bot,
  Github,
} from "lucide-react";

export type ProductBucket =
  | "profitable"
  | "underperforming"
  | "low-volume"
  | "high-cost";

export interface ProductData {
  productTitle: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  value: number;
  ctr: number;
  roas: number;
  cvr: number;
  cpa: number;
  bucket?: ProductBucket;
}

export interface BucketSummary {
  bucket: ProductBucket;
  name: string;
  description: string;
  count: number;
  totalCost: number;
  totalValue: number;
  totalConversions: number;
  roas: number;
  color: string;
  icon: JSX.Element;
  products: ProductData[];
}

export interface NGram {
  text: string;
  frequency: number;
  totalCost: number;
  totalValue: number;
  avgROAS: number;
  avgCPA: number;
  performanceScore: number;
}

export interface Product {
  id: string;
  tier: "foundation" | "learning" | "community" | "elite" | "free";
  type:
    | "script"
    | "course"
    | "workshop"
    | "membership"
    | "bundle"
    | "premium"
    | "tool";
  name: string;
  description?: string;
  price: string; // Price is now always a string
  memberPrice?: string; // Add member pricing
  priceNote?: string;
  link: string;
  detailsLink?: string; // Optional link to details page
  extras?: string[];
  highlight?: boolean;
  soldOut?: boolean;
  limited?: boolean;
  limitedCount?: number;
  icon?: React.ElementType;
  themeColor?: string; // e.g., 'blue', 'orange', 'purple', 'green'
  featured?: boolean;
  bestSeller?: boolean;
  rating?: number;
  ratingCount?: number;
  discount?: {
    original: number | string;
    percentage: number;
  };
  earlyBird?: boolean; // Add early bird flag
  earlyBirdEndDate?: string; // Add early bird end date
}

// --- Product Data ---

// TIER 4: ELITE LEVEL
export const OWN_THE_AGENT: Product = {
  id: "premium-own-the-agent",
  tier: "elite",
  type: "premium",
  name: "Own the Agent™",
  price: "From €999/month",
  priceNote: "Limited to 15 members only",
  link: "/own",
  description: "Premium transformation & 1-on-1 consulting",
  extras: [
    "Very small group (max 15 members)",
    "1-on-1 calls with Mike",
    "Help with AI implementation for your business",
    "Priority access to ALL new products",
    "Direct email access",
    "Monthly strategy calls",
    "Exclusive workshops",
    "All scripts & workshops included",
    "Business transformation conversations",
    "Lifetime updates & support",
  ],
  highlight: true,
  limited: true,
  limitedCount: 15,
  icon: Crown,
  themeColor: "indigo",
  featured: true,

  discount: {
    original: 1499,
    percentage: 33,
  },
};

// TIER 3: COMMUNITY LEVEL
export const FROM_ADS_TO_AI: Product = {
  id: "membership-ads-to-ai",
  tier: "community",
  type: "membership",
  name: "From Ads to AI Community",
  price: "€799/year",
  link: "/ai",
  description:
    "Join a thriving community of business owners, marketers, and Google Ads professionals making the transition to AI-enhanced work. Get monthly masterclass webinars with the latest strategies, access to a private community where members share insights and get help, plus the full 8020brain business operating system and the MCC version of the 80/20 Agent. Every member also gets a 25-minute strategy call at signup and the full library of professional scripts and courses.",
  extras: [
    "Full 8020brain business operating system",
    "Agency Brain app + up to 5 team members",
    "Monthly Masterclass Webinars",
    "Weekly 'AI in Action' Videos",
    "Private Community Access",
    "MCC version of 80/20 Agent (manage multiple accounts)",
    "All Professional Scripts (PMax, MCC PMax, Negative Keywords)",
    "Scripts & Sheets Mastery 2025 course",
    "25-minute Strategy Call at signup",
  ],
  highlight: true,
  icon: Users,
  themeColor: "purple",
};

// TIER 2: LEARNING LEVEL
export const BUILD_THE_AGENT: Product = {
  id: "workshop-build-the-agent",
  tier: "learning",
  type: "workshop",
  name: "Build the Agent Bootcamp",
  price: "€1,399",
  priceNote: "8-week live training, custom AI agent, lifetime access",
  link: "/build",
  description:
    "Transform your career with AI in this intensive 8-week live bootcamp. Learn to build custom AI agents like the 80/20 Agent through small group training via Zoom. You'll get a custom AI agent included, learn to build tools that can analyze data, generate insights, and automate complex tasks. This isn't just learning - it's hands-on project development with expert guidance that gives you career-transforming skills.",
  extras: [
    "Small Group Live Training (via Zoom)",
    "Custom AI Agent Included",
    "Learn to build tools like 80/20 Agent",
    "Scripts & Sheets Mastery 2025 included",
    "Bonus scripts, templates & prompts",
    "Private community access",
    "Follow-up Q&A sessions",
    "Lifetime access to recordings",
    "Expert guidance and feedback",
  ],
  icon: School,
  themeColor: "pink",
  bestSeller: true,
  // earlyBird: true,
  // earlyBirdEndDate: "June 18",
};

export const SCRIPTS_MASTERY: Product = {
  id: "course-scripts-mastery",
  tier: "learning",
  type: "course",
  name: "Scripts & Sheets Mastery 2025",
  price: "€249",
  memberPrice: "Free",
  link: "/mastery",
  detailsLink: "/mastery",
  description:
    "Learn to create Google Ads Scripts, MCC Scripts & Google Apps Scripts with AI",
  extras: [
    "9 modules, 3+ hours of training",
    "MCC Scripts for the first time",
    "Google Apps Script automation",
    "AI-powered script writing techniques",
    "Complete code repository included",
    "Lifetime access to updates",
  ],
  icon: BookOpen,
  themeColor: "pink",
  rating: 4.7,
  ratingCount: 152,
};

// TIER 1: FOUNDATION LEVEL
export const MCC_PMAX_SCRIPT: Product = {
  id: "script-mcc-pmax",
  tier: "foundation",
  type: "script",
  name: "MCC PMax Script",
  price: "$399",
  link: "https://mikerhodes.circle.so/checkout/latest-mcc-script",
  description: "Performance Max insights for unlimited accounts",
  extras: [
    "Create PMax sheets for all accounts",
    "Includes negative keyword script!",
  ],
  icon: Wrench,
  themeColor: "blue",
  featured: false,
  rating: 4.9,
  ratingCount: 843,
  bestSeller: true,
};

export const SINGLE_PMAX_SCRIPT: Product = {
  id: "script-single-pmax",
  tier: "foundation",
  type: "script",
  name: "Single Account PMax Script",
  price: "$199",
  link: "/pmax",
  description: "Performance Max insights for one account",
  extras: [
    "Create PMax Insights in seconds",
    "Free training videos, support & updates",
  ],
  icon: Wrench,
  themeColor: "blue",
  rating: 4.8,
  ratingCount: 1114,
};

export const NEGATIVE_KEYWORD_SCRIPT: Product = {
  id: "script-negative-keyword",
  tier: "foundation",
  type: "script",
  name: "Negative Keyword Script",
  price: "$109",
  link: "https://mikerhodes.circle.so/checkout/negative-keyword-script",
  description: "Automated negative keyword management",
  extras: ["Works with Search campaigns", "Visualise your data in new ways"],
  icon: Wrench,
  themeColor: "blue",
  rating: 4.6,
  ratingCount: 35,
};

// BUNDLE (Special) not currently in use
export const MEGA_BUNDLE: Product = {
  id: "bundle-mega",
  tier: "foundation",
  type: "bundle",
  name: "MASSIVE VALUE PACKAGE 🤪",
  price: "€499",
  link: "https://mikerhodes.circle.so/checkout/massive-bundle",
  description: "Get all scripts and courses at a crazy discount",
  extras: [
    "MCC and PMax scripts",
    "Scripts & Sheets Mastery",
    "Negative Keyword Script",
    "Doesn't include Build the Agent™",
    "Get 9 bonus courses",
    "Priority support",
    "Save over €400",
    "Lifetime access to future updates",
  ],
  highlight: true,
  icon: Package,
  themeColor: "blue",
  rating: 5,
  ratingCount: 78,
  discount: {
    original: 799,
    percentage: 38,
  },
};

// A2AI Solo + Coaching bundle (canonical source: context/business/pricing.md)
export const IMPROVED_BUNDLE: Product = {
  id: "bundle-solo-coaching",
  tier: "community",
  type: "bundle",
  name: "A2AI Solo + Coaching",
  price: "€999",
  priceNote: "Renews at €799/year for the membership",
  link: "https://mikerhodes.circle.so/checkout/a2ai-coaching",
  description:
    "Full A2AI Solo membership plus an extra hour of 1-on-1 coaching with me, delivered as two 30-minute calls. On top of the free 25-minute strategy call every member gets. Priced at the sign-up bundle rate of €200/hr versus the retail rate of €350/hr.",
  extras: [
    "Everything in A2AI Solo (membership, brain, scripts, courses, MCC tool)",
    "Free 25-minute strategy call at signup",
    "2 x 30-minute coaching calls with Mike (1 hour total extra)",
    "Use the calls for onboarding, brain setup, or anything you want help with",
  ],
  discount: {
    original: "€1,149",
    percentage: 13,
  },
  rating: 5,
  ratingCount: 78,
  highlight: true,
  icon: Package,
  themeColor: "orange",
};

// --- FREE TOOLS ---
export const EIGHTY_TWENTY_AGENT: Product = {
  id: "tool-8020agent",
  tier: "free",
  type: "tool",
  name: "80/20 Agent",
  price: "Free",
  link: "https://8020agent.com",
  description: "AI analysis for 5 accounts (unlimited for members)",
  extras: [
    "Connect 5 Google Ads accounts",
    "Profitability analysis",
    "Optimization recommendations",
    "Time-saving insights",
    "Unlimited access for community members",
  ],
  icon: Bot,
  themeColor: "green",
};

export const FREE_SCRIPTS: Product = {
  id: "tool-free-scripts",
  tier: "free",
  type: "tool",
  name: "Free Google Ads Scripts",
  price: "Free",
  link: "https://github.com/mikerhodesideas/free",
  description: "A collection of useful scripts to boost your productivity.",
  extras: [
    "Scripts to test API Keys",
    "PMax Search Terms Script",
    "Black Friday Dashboard",
    "PMax Placement Script",
    "N-Gram Analyzer",
    "Landing Page Script",
    "Explore more on GitHub",
  ],
  icon: Github,
  themeColor: "green",
};

// --- Comparison Table Features Data START ---

// Feature interface
export interface Feature {
  id: string;
  name: string;
  description?: string;
  foundation: boolean | "partial" | "upgradable"; // Foundation level (for old page)
  learning: boolean | "partial" | "upgradable"; // Learning level (for old page)
  community: boolean | "partial" | "upgradable"; // Community level (for old page)
  elite: boolean | "partial" | "upgradable"; // Elite level (for old page)
  scripts: boolean | "partial" | "upgradable"; // Ready-Made Tools path (for new page)
  bundle: boolean | "partial" | "upgradable"; // Massive Value Package (for new page)
  category: "tools" | "learning" | "community" | "support" | "access";
}

// Features list
export const features: Feature[] = [
  // Ready-Made Tools & Scripts
  {
    id: "eighty-twenty-agent",
    name: "80/20 Agent Access",
    description: "AI-powered analysis for Google Ads campaigns",
    foundation: "partial", // Limited to 5 accounts
    learning: false,
    community: true, // Unlimited access
    elite: true,
    scripts: "partial", // Limited to 5 accounts
    bundle: true,
    category: "tools",
  },
  {
    id: "neg-keyword-script",
    name: "Negative Keyword Script",
    description: "Ready-to-use script for negative keyword management",
    foundation: true,
    learning: false,
    community: false,
    elite: true,
    scripts: true,
    bundle: true,
    category: "tools",
  },
  {
    id: "pmax-script-single",
    name: "Single Account PMax Script",
    description: "Performance Max insights for one account",
    foundation: true,
    learning: false,
    community: false,
    elite: true,
    scripts: true,
    bundle: true,
    category: "tools",
  },
  {
    id: "pmax-script-mcc",
    name: "MCC PMax Script",
    description: "Performance Max insights for unlimited accounts",
    foundation: true,
    learning: false,
    community: false,
    elite: true,
    scripts: true,
    bundle: true,
    category: "tools",
  },

  // Learning & Development
  {
    id: "script-mastery",
    name: "Scripts & Sheets Mastery 2025",
    description:
      "Learn to create Google Ads Scripts, MCC Scripts & Google Apps Scripts with AI",
    foundation: false,
    learning: true,
    community: "partial", // Free for members
    elite: true,
    scripts: false,
    bundle: true,
    category: "learning",
  },
  {
    id: "build-agent",
    name: "Build the Agent Bootcamp",
    description: "8-week live training to build custom AI agents",
    foundation: false,
    learning: true,
    community: false,
    elite: true,
    scripts: false,
    bundle: true,
    category: "learning",
  },
  {
    id: "monthly-masterclass",
    name: "Monthly Masterclasses",
    description: "Live workshops on advanced AI and Google Ads topics",
    foundation: false,
    learning: false,
    community: true,
    elite: true,
    scripts: false,
    bundle: true,
    category: "learning",
  },

  // Community & Support
  {
    id: "private-community",
    name: "Private Community Access",
    description: "Connect with peers, ask questions, and share insights",
    foundation: false,
    learning: false,
    community: true,
    elite: true,
    scripts: false,
    bundle: true,
    category: "community",
  },
  {
    id: "ai-insights",
    name: "Weekly AI Insights",
    description: "Stay updated with the latest AI developments",
    foundation: false,
    learning: false,
    community: true,
    elite: true,
    scripts: false,
    bundle: true,
    category: "community",
  },
  {
    id: "priority-support",
    name: "Priority Support",
    description: "Get faster responses to your questions and issues",
    foundation: false,
    learning: false,
    community: true,
    elite: true,
    scripts: false,
    bundle: true,
    category: "support",
  },

  // Access & Updates
  {
    id: "lifetime-updates",
    name: "Lifetime Updates",
    description: "Receive all future updates for purchased products",
    foundation: true,
    learning: true,
    community: true,
    elite: true,
    scripts: true,
    bundle: true,
    category: "access",
  },
  {
    id: "early-access",
    name: "Early Access to New Products",
    description: "Get early access to new features and products",
    foundation: false,
    learning: false,
    community: true,
    elite: true,
    scripts: false,
    bundle: true,
    category: "access",
  },
  {
    id: "custom-prompts",
    name: "Custom Prompt Library",
    description: "Access to proven AI prompts and templates",
    foundation: false,
    learning: false,
    community: true,
    elite: true,
    scripts: false,
    bundle: true,
    category: "access",
  },
];

// --- Comparison Table Features Data END ---
